import React from 'react';
import {
  createProgressReport,
  createSession,
  deleteSession,
  finishSession,
  getProgressReport,
  getSession,
  listProgressReports,
  listSessions,
  regenerateSession,
} from './api';
import { Brand, Transcript } from './components';
import { loadPending, removePending, type PendingFinish } from './pending';
import { CopyDownload, FeedbackBody, StatusChip, TranscriptToggle } from './feedback';
import { deleteHistoryEntry, loadHistory, type HistoryEntry } from './history';
import { Markdown } from './markdown';
import { RubricDetails } from './Rubric';
import { ROLE_LABELS } from './scenarios';
import {
  CANDIDATE_PROFILE_OPTIONS,
  PERSONAS,
  SCENARIOS,
  type ProgressReportRecord,
  type SessionRecord,
  type SessionSummary,
  type TranscriptEntry,
} from './types';

const POLL_MS = 10_000;
/** A 'live' session untouched this long was closed without a summary (autosave runs every ~20 s). */
const STALE_LIVE_MS = 3 * 60_000;

function isStaleLive(session: Pick<SessionSummary, 'status' | 'updatedAt'>): boolean {
  return session.status === 'live' && Date.now() - session.updatedAt > STALE_LIVE_MS;
}

function scenarioLabel(id: string): string {
  return SCENARIOS.find((item) => item.value === id)?.label ?? id;
}

function describe(session: Pick<SessionSummary, 'scenario' | 'persona' | 'candidateProfile' | 'durationMinutes'>): string {
  const persona = PERSONAS.find((item) => item.id === session.persona)?.name ?? session.persona;
  const parts = [scenarioLabel(session.scenario), persona];
  if (session.scenario === 'roleplay_screening') {
    parts.push(CANDIDATE_PROFILE_OPTIONS.find((item) => item.value === session.candidateProfile)?.label ?? session.candidateProfile);
  }
  parts.push(`${session.durationMinutes} min`);
  return parts.join(' · ');
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong. Try again.';
}

function savedTranscript(record: SessionRecord): TranscriptEntry[] {
  return record.transcript.map((line, index) => ({
    id: `t${index}`,
    role: line.role === 'ai' ? 'interviewer' : 'candidate',
    status: 'complete',
    text: line.text,
  }));
}

function SessionDetail({ id, onDeleted }: { id: string; onDeleted: (id: string) => void }) {
  const [record, setRecord] = React.useState<SessionRecord | null>(null);
  const [error, setError] = React.useState('');
  const [nonce, setNonce] = React.useState(0);

  React.useEffect(() => {
    let stopped = false;
    let timer = 0;
    setError('');
    const tick = () => {
      getSession(id)
        .then((next) => {
          if (stopped) return;
          setRecord(next);
          if (next.status === 'live' || next.status === 'debriefing') timer = window.setTimeout(tick, POLL_MS);
        })
        .catch((loadError: unknown) => {
          if (!stopped) setError(errorText(loadError));
        });
    };
    tick();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [id, nonce]);

  if (error) return <div className="form-error" role="alert">{error}</div>;
  if (!record) return <p className="hint" role="status">Loading…</p>;
  const labels = ROLE_LABELS[record.scenario];

  return (
    <article className="prep-content progress-detail">
      <h2>{describe(record)} <StatusChip status={record.status} /></h2>
      <p className="hint">{new Date(record.createdAt).toLocaleString()}</p>
      {record.status === 'debriefing' && (
        <p className="generating-note" role="status">
          Feedback is being generated on the server — check back in a few minutes.
          {Date.now() - record.updatedAt > 8 * 60_000 && ' This is taking longer than usual; it will still appear here.'}
        </p>
      )}
      {record.status === 'live' && (
        <div className="generating-note" role="status">
          <p>{isStaleLive(record) ? 'Ended without summary.' : 'This session is still live.'}</p>
          {isStaleLive(record) && (
            <button
              className="primary-button primary-button--coral"
              onClick={() => {
                void finishSession(id, { transcript: record.transcript, notes: record.notes })
                  .then((next) => {
                    setRecord(next);
                    setNonce((count) => count + 1);
                  })
                  .catch((finishError: unknown) => setError(errorText(finishError)));
              }}
              type="button"
            >
              Generate feedback
            </button>
          )}
        </div>
      )}
      {record.status === 'failed' && (
        <div className="form-error failed-box" role="alert">
          <p>{record.error || 'The feedback could not be generated.'}</p>
          <button
            className="primary-button primary-button--coral"
            onClick={() => {
              void regenerateSession(id)
                .then((next) => {
                  setRecord(next);
                  setNonce((count) => count + 1);
                })
                .catch((regenError: unknown) => setError(errorText(regenError)));
            }}
            type="button"
          >
            Retry feedback
          </button>
        </div>
      )}
      {record.status === 'done' && record.debriefMarkdown && <FeedbackBody markdown={record.debriefMarkdown} />}
      {record.status === 'done' && <RubricDetails />}
      <div className="debrief-actions">
        {record.status === 'done' && record.debriefMarkdown && (
          <CopyDownload filename={`feedback-${new Date(record.createdAt).toISOString().slice(0, 10)}.md`} markdown={record.debriefMarkdown} />
        )}
        {record.status === 'done' && (
          <button
            onClick={() => {
              void regenerateSession(id)
                .then((next) => {
                  setRecord(next);
                  setNonce((count) => count + 1);
                })
                .catch((regenError: unknown) => setError(errorText(regenError)));
            }}
            type="button"
          >
            Regenerate
          </button>
        )}
        <button
          onClick={() => {
            if (!window.confirm('Delete this session and its feedback?')) return;
            void deleteSession(id)
              .then(() => onDeleted(id))
              .catch((deleteError: unknown) => setError(errorText(deleteError)));
          }}
          type="button"
        >
          Delete
        </button>
      </div>
      {record.transcript.length > 0 && (
        <TranscriptToggle count={record.transcript.length}>
          <Transcript
            emptyText="No transcript."
            entries={savedTranscript(record)}
            labelFor={
              record.scenario === 'roleplay_screening'
                ? (entry) =>
                    entry.role === 'candidate'
                      ? labels.user
                      : record.transcript[Number(entry.id.slice(1))]?.phase === 'screening'
                        ? 'Candidate'
                        : 'Tania'
                : undefined
            }
            labels={{ ai: labels.ai, user: labels.user }}
          />
        </TranscriptToggle>
      )}
    </article>
  );
}

function ProgressReports() {
  const [reports, setReports] = React.useState<ProgressReportRecord[]>([]);
  const [openId, setOpenId] = React.useState<string | null>(null);
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(() => {
    listProgressReports()
      .then((response) => {
        setReports(response.reports);
        setOpenId((current) => current ?? response.reports[0]?.id ?? null);
      })
      .catch((loadError: unknown) => setError(errorText(loadError)));
  }, []);
  React.useEffect(load, [load]);

  const pending = reports.find((report) => report.status === 'pending');
  React.useEffect(() => {
    if (!pending) return undefined;
    const timer = window.setTimeout(() => {
      getProgressReport(pending.id)
        .then((next) => setReports((current) => current.map((item) => (item.id === next.id ? next : item))))
        .catch(() => undefined);
    }, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [pending, reports]);

  const open = reports.find((report) => report.id === openId) ?? null;

  return (
    <section className="progress-reports">
      <button
        className="primary-button primary-button--coral"
        disabled={busy || Boolean(pending)}
        onClick={() => {
          setBusy(true);
          setError('');
          createProgressReport()
            .then((created) => {
              setReports((current) => [created, ...current]);
              setOpenId(created.id);
            })
            .catch((createError: unknown) => setError(errorText(createError)))
            .finally(() => setBusy(false));
        }}
        type="button"
      >
        {pending ? 'Generating report…' : 'Generate progress report'}
      </button>
      {pending && <p className="hint" role="status">The report is being written on the server — you can close this page and come back.</p>}
      {error && <div className="form-error" role="alert">{error}</div>}
      {reports.length > 1 && (
        <div className="report-chips" role="tablist" aria-label="Progress reports">
          {reports.map((report) => (
            <button
              aria-selected={report.id === openId}
              className={report.id === openId ? 'is-active' : ''}
              key={report.id}
              onClick={() => setOpenId(report.id)}
              role="tab"
              type="button"
            >
              {new Date(report.createdAt).toLocaleDateString()} · {report.status === 'pending' ? 'generating' : report.status}
            </button>
          ))}
        </div>
      )}
      {open?.status === 'done' && open.markdown && (
        <article className="prep-content">
          <Markdown source={open.markdown} />
          <div className="debrief-actions">
            <CopyDownload filename={`progress-${new Date(open.createdAt).toISOString().slice(0, 10)}.md`} markdown={open.markdown} />
          </div>
        </article>
      )}
      {open?.status === 'failed' && <div className="form-error" role="alert">{open.error || 'The report could not be generated.'}</div>}
    </section>
  );
}

export function Sessions({ backLabel, embedded = false, onBack }: { backLabel: string; embedded?: boolean; onBack: () => void }) {
  const [sessions, setSessions] = React.useState<SessionSummary[] | null>(null);
  const [error, setError] = React.useState('');
  const [openId, setOpenId] = React.useState<string | null>(null);
  const [local, setLocal] = React.useState<HistoryEntry[]>(loadHistory);
  const [pending, setPending] = React.useState<PendingFinish[]>(loadPending);
  const [resendError, setResendError] = React.useState('');
  const [openLocal, setOpenLocal] = React.useState<HistoryEntry | null>(null);

  const [nonce, setNonce] = React.useState(0);
  const inFlight = sessions?.some((item) => item.status === 'live' || item.status === 'debriefing') ?? false;
  React.useEffect(() => {
    let stopped = false;
    let timer = 0;
    const tick = () => {
      listSessions()
        .then((response) => {
          if (stopped) return;
          setSessions(response.sessions);
          setError('');
          if (response.sessions.some((item) => item.status === 'live' || item.status === 'debriefing')) {
            timer = window.setTimeout(tick, POLL_MS);
          }
        })
        .catch((loadError: unknown) => {
          if (stopped) return;
          setError(errorText(loadError));
          setSessions((current) => current ?? []);
        });
    };
    tick();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [inFlight, nonce]);

  return (
    <main className={embedded ? 'prep-page progress-page prep-page--embedded' : 'prep-page progress-page'}>
      {!embedded && (
        <header className="app-header">
          <Brand compact />
          <button className="text-button" onClick={onBack} type="button">{backLabel}</button>
        </header>
      )}
      <section className="progress-layout">
        <div className="prep-heading">
          <p className="eyebrow">Sessions &amp; progress</p>
          <h1>Your practice record</h1>
          <p>Every session is saved on the server with its feedback. Feedback arrives a few minutes after a call ends.</p>
        </div>

        <div className="progress-side">
          {error && <div className="form-error" role="alert">{error}</div>}
          {sessions === null && <p className="hint" role="status">Loading sessions…</p>}
          {sessions?.length === 0 && !error && <p className="hint">No sessions yet. Finish a practice call and it appears here.</p>}
          <ul className="material-list">
            {sessions?.map((session) => (
              <li className={`material-row ${openId === session.id ? 'is-open' : ''}`} key={session.id}>
                <button
                  aria-expanded={openId === session.id}
                  className="material-name session-row"
                  onClick={() => {
                    setOpenLocal(null);
                    setOpenId(openId === session.id ? null : session.id);
                  }}
                  type="button"
                >
                  <span className="session-row-head">
                    <strong>{new Date(session.createdAt).toLocaleString()}</strong>
                    <StatusChip status={session.status} />
                  </span>
                  <small>{describe(session)}</small>
                  {session.topImprovement && <small className="session-top">{session.topImprovement}</small>}
                  {isStaleLive(session) && <small className="stale-note">Ended without summary — open to generate feedback</small>}
                </button>
              </li>
            ))}
          </ul>

          {pending.length > 0 && (
            <>
              <h3 className="side-heading">Not sent yet (this device)</h3>
              {resendError && <div className="form-error" role="alert">{resendError}</div>}
              <ul className="material-list">
                {pending.map((item) => (
                  <li className="material-row" key={item.sessionId}>
                    <div className="material-row-head">
                      <span className="material-name">
                        <strong>{new Date(item.savedAt).toLocaleString()}</strong>
                        <small>{scenarioLabel(item.setup.scenario)} · transcript saved locally</small>
                      </span>
                      <button
                        className="ghost-button"
                        onClick={() => {
                          setResendError('');
                          const send = item.sessionId
                            ? finishSession(item.sessionId, item.body)
                            : createSession(item.setup).then((created) => finishSession(created.id, item.body));
                          void send
                            .then(() => {
                              removePending(item.sessionId);
                              setPending(loadPending());
                              setSessions(null);
                              setNonce((count) => count + 1);
                            })
                            .catch((sendError: unknown) => setResendError(errorText(sendError)));
                        }}
                        type="button"
                      >
                        Resend for feedback
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}

          {local.length > 0 && (
            <>
              <h3 className="side-heading">Local (this device)</h3>
              <ul className="material-list">
                {local.map((entry) => (
                  <li className="material-row" key={entry.id}>
                    <div className="material-row-head">
                      <button
                        className="material-name"
                        onClick={() => {
                          setOpenId(null);
                          setOpenLocal(entry);
                        }}
                        type="button"
                      >
                        <strong>{new Date(entry.date).toLocaleString()}</strong>
                        <small>{scenarioLabel(entry.scenario)} · {entry.practisedMinutes || entry.durationMinutes} min</small>
                      </button>
                      <button
                        aria-label={`Delete local session from ${new Date(entry.date).toLocaleDateString()}`}
                        className="icon-button"
                        onClick={() => {
                          if (!window.confirm('Delete this local session?')) return;
                          setLocal(deleteHistoryEntry(entry.id));
                          if (openLocal?.id === entry.id) setOpenLocal(null);
                        }}
                        type="button"
                      >
                        ×
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="progress-main">
          {openId && (
            <SessionDetail
              id={openId}
              key={openId}
              onDeleted={(id) => {
                setSessions((current) => current?.filter((item) => item.id !== id) ?? null);
                setOpenId(null);
              }}
            />
          )}
          {openLocal && (
            <article className="prep-content progress-detail">
              <h2>{scenarioLabel(openLocal.scenario)} <span className="status-chip status-chip--done">Local</span></h2>
              <FeedbackBody markdown={openLocal.debriefMarkdown} />
              <div className="debrief-actions">
                <CopyDownload filename={`debrief-${openLocal.date.slice(0, 10)}.md`} markdown={openLocal.debriefMarkdown} />
              </div>
              {openLocal.transcript && (
                <details className="saved-transcript">
                  <summary>Transcript</summary>
                  <pre>{openLocal.transcript}</pre>
                </details>
              )}
            </article>
          )}
          <ProgressReports />
        </div>
      </section>
    </main>
  );
}
